import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import DatePicker from '../components/common/DatePicker';
import AbaProtegida from '../components/common/AbaProtegida';
import { pedidosService, Pedido } from '../services/pedidosService';
import { ValidationService } from '../services/validationService';
import { UploadService } from '../services/uploadService';
import NotificacoesService from '../services/notificacoesService';
import { AuditService } from '../services/auditService';
import { TipoAcaoAudit } from '../types';
import CheckboxD2 from '../components/CheckboxD2';

interface PedidoComSolicitacao extends Pedido {
  solicitacao?: {
    id: string;
    codigo: string;
    data_desejada: string;
    fornecedor: string;
    peso: number;
    volume: number;
    status: string;
    solicitante?: {
      nome: string;
      email: string;
    };
    documentos?: any[];
  };
}


const Pedidos: React.FC = () => {
  const { user } = useAuth();
  const { filialAtual } = useApp();
  const [pedidos, setPedidos] = useState<PedidoComSolicitacao[]>([]);
  const [pedidosPendentes, setPedidosPendentes] = useState<PedidoComSolicitacao[]>([]);
  const [pedidosDigitados, setPedidosDigitados] = useState<PedidoComSolicitacao[]>([]);
  const [filteredPedidos, setFilteredPedidos] = useState<PedidoComSolicitacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmarPedido, setConfirmarPedido] = useState<PedidoComSolicitacao | null>(null);
  const [numeroPedido, setNumeroPedido] = useState('');
  const [arquivoSelecionado, setArquivoSelecionado] = useState<File | null>(null);
  const [showConfirmarModal, setShowConfirmarModal] = useState(false);
  const [editingPedido, setEditingPedido] = useState<PedidoComSolicitacao | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState<'pendentes' | 'digitados' | 'todos' | 'checkbox_d2'>('pendentes');
  const [filters, setFilters] = useState({
    status: '',
    fornecedor: '',
    data: ''
  });
  const [numpedPesquisa, setNumpedPesquisa] = useState('');
  const [produtosPedido, setProdutosPedido] = useState<any[]>([]);
  const [loadingProdutos, setLoadingProdutos] = useState(false);

  const handleBuscarProdutosPedido = async () => {
    if (!numpedPesquisa.trim()) {
      alert('Informe o número do pedido!');
      return;
    }

    setLoadingProdutos(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/pedidos-compra/pedido/${numpedPesquisa}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Frontend-Secret': process.env.REACT_APP_FRONTEND_SECRET || ''
        }
      });

      const result = await response.json();
      
      if (result.success && result.data) {
        setProdutosPedido(result.data);
      } else {
        setProdutosPedido([]);
        alert('Nenhum produto encontrado para este pedido');
      }
    } catch (error) {
      console.error('Erro ao buscar produtos do pedido:', error);
      alert('Erro ao buscar produtos do pedido');
      setProdutosPedido([]);
    } finally {
      setLoadingProdutos(false);
    }
  };

  useEffect(() => {
    carregarPedidos();
  }, [filialAtual]);

  useEffect(() => {
    // Aplicar filtros
    let filtered = abaAtiva === 'pendentes' ? pedidosPendentes : (abaAtiva === 'digitados' ? pedidosDigitados : pedidos);

    if (filters.status) {
      filtered = filtered.filter(p => p.status === filters.status);
    }

    if (filters.fornecedor) {
      filtered = filtered.filter(p =>
        p.fornecedor?.toLowerCase().includes(filters.fornecedor.toLowerCase())
      );
    }

    if (filters.data) {
      filtered = filtered.filter(p => p.data_desejada === filters.data);
    }

    setFilteredPedidos(filtered);
  }, [filters, pedidos, pedidosPendentes, pedidosDigitados, abaAtiva]);

  const carregarPedidos = async () => {
    setLoading(true);
    try {
      // Carregar pedidos pendentes
      const { data: pendentes } = await pedidosService.buscarPedidosPendentes(filialAtual);
      setPedidosPendentes(pendentes || []);

      // Carregar pedidos digitados
      const { data: digitados } = await pedidosService.buscarPedidos({ filial: filialAtual, status: ['Pedido digitado'] });
      setPedidosDigitados(digitados || []);

      // Carregar todos os pedidos
      const { data: todos } = await pedidosService.buscarPedidos({ filial: filialAtual });
      setPedidos(todos || []);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarPedido = (pedido: PedidoComSolicitacao) => {
    setConfirmarPedido(pedido);
    setNumeroPedido('');
    setShowConfirmarModal(true);
  };

  const handleSalvarConfirmacao = async () => {
    if (!confirmarPedido) {
      alert('Informe o pedido!');
      return;
    }

    if (!numeroPedido.trim()) {
      alert('Informe o número do pedido!');
      return;
    }

    try {
      // Validar confirmação de pedido
      const pedidoParaValidar: any = {
        id: confirmarPedido.id,
        numero_pedido: confirmarPedido.numero_pedido,
        solicitacao_id: confirmarPedido.solicitacao_id,
        fornecedor: confirmarPedido.fornecedor || '',
        data_desejada: confirmarPedido.data_desejada || '',
        peso: confirmarPedido.peso || 0,
        volume: confirmarPedido.volume?.toString() || '',
        tipo_produto: '',
        tipo_carga: '',
        observacoes: confirmarPedido.observacoes,
        status: confirmarPedido.status === 'Aguardando Pedido' ? 'Aguardando pedido' : 'Pedido digitado',
        criado_em: new Date().toISOString(),
        atualizado_em: new Date().toISOString(),
        criado_por: user?.id || '',
        prioridade: confirmarPedido.prioridade,
        documentos: [],
        motivo_reprovacao: undefined
      };
      
      const validacao = ValidationService.validarConfirmacaoPedido(
        pedidoParaValidar,
        numeroPedido,
        arquivoSelecionado || undefined
      );
      
      if (!validacao.isValid) {
        const mensagem = ValidationService.formatarMensagens(validacao);
        alert(`Não é possível confirmar este pedido:\n\n${mensagem}`);
        return;
      }
      
      // Mostrar avisos se houver
      if (validacao.warnings && validacao.warnings.length > 0) {
        const avisos = validacao.warnings.join('\n  - ');
        const confirmar = window.confirm(`Atenção:\n  - ${avisos}\n\nDeseja continuar com a confirmação?`);
        if (!confirmar) return;
      }

      // Fazer upload real do arquivo
      if (arquivoSelecionado) {
        const uploadResult = await UploadService.uploadPedidoFile(
          arquivoSelecionado,
          confirmarPedido.id
        );

        if (!uploadResult.success) {
          alert('Erro ao fazer upload do arquivo: ' + uploadResult.error);
          setLoading(false);
          return;
        }

        // Adicionar documento digitado ao pedido
        await pedidosService.adicionarDocumentoDigitado(
          confirmarPedido.id,
          {
            nome: arquivoSelecionado.name,
            tipo: arquivoSelecionado.type,
            databaseKey: uploadResult.fileName || '',
            url: uploadResult.fileUrl,
            tamanho: arquivoSelecionado.size,
            uploadedAt: new Date().toISOString(),
            uploadedBy: user?.id
          },
          user?.id
        );

        // 🆕 SINCRONIZAR: Copiar documento para a solicitação original
        if (confirmarPedido.solicitacao_id) {
          try {
            const { SincronizacaoDocumentosService } = require('../services/sincronizacaoDocumentosService');
            
            // Sincronizar o documento com a solicitação original
            await SincronizacaoDocumentosService.sincronizarDocumentoPedidoComSolicitacao(
              confirmarPedido.solicitacao_id,
              {
                nome: arquivoSelecionado.name,
                tipo: arquivoSelecionado.type,
                tamanho: arquivoSelecionado.size,
                storagePath: uploadResult.fileName,
                url: uploadResult.fileUrl,
                uploadedBy: user?.id || '',
                fornecedorMercadoria: confirmarPedido.fornecedor || ''
              }
            );

          } catch (syncError) {
          }
        }

        // Atualizar status do pedido e da solicitação
        await pedidosService.confirmarPedido(
          confirmarPedido.id,
          numeroPedido,
          uploadResult.fileName ? uploadResult.fileName.replace('pedido-', '').replace('.pdf', '') : '',
          user?.id
        );

        // Registrar no audit trail
        await AuditService.registrarAcao({
          usuarioId: user?.id || '',
          usuarioNome: user?.nome || '',
          acao: TipoAcaoAudit.ATUALIZAR,
          tabela: 'pedidos',
          registroId: confirmarPedido.id,
          valoresAntigos: { numero_pedido: confirmarPedido.numero_pedido, status: confirmarPedido.status },
          valoresNovos: { numero_pedido: numeroPedido, status: 'Pedido digitado' }
        });
      } else {
        // Se não houver arquivo, confirmar sem documento
        const confirmarSemArquivo = window.confirm(
          'Nenhum arquivo foi anexado. Deseja confirmar o pedido mesmo assim?'
        );
        
        if (!confirmarSemArquivo) return;
        
        await pedidosService.confirmarPedido(
          confirmarPedido.id,
          numeroPedido,
          '',
          user?.id
        );

        // Registrar no audit trail
        await AuditService.registrarAcao({
          usuarioId: user?.id || '',
          usuarioNome: user?.nome || '',
          acao: TipoAcaoAudit.ATUALIZAR,
          tabela: 'pedidos',
          registroId: confirmarPedido.id,
          valoresAntigos: { numero_pedido: confirmarPedido.numero_pedido, status: confirmarPedido.status },
          valoresNovos: { numero_pedido: numeroPedido, status: 'Pedido digitado' }
        });
      }
      
      alert('Pedido confirmado com sucesso!');
      
      // Enviar notificação ao solicitante
      if (confirmarPedido.solicitacao_id && (confirmarPedido as any).solicitante_id) {
        await NotificacoesService.notificarPedidoDigitado(
          (confirmarPedido as any).solicitante_id,
          confirmarPedido.solicitacao_id,
          numeroPedido
        );
      }
      
      setShowConfirmarModal(false);
      setConfirmarPedido(null);
      setNumeroPedido('');
      setArquivoSelecionado(null);
      carregarPedidos();
    } catch (error) {
      console.error('Erro no handleSalvarConfirmacao:', error);
      alert('Erro ao confirmar pedido!');
    }
  };

  const handleAprovarPedido = async (pedido: PedidoComSolicitacao) => {
    if (!window.confirm(`Deseja aprovar o pedido ${pedido.numero_pedido || pedido.codigo} e converter em carga?`)) {
      return;
    }

    try {
      setLoading(true);

      // Aprovar o pedido (atualizar status)
      await pedidosService.atualizarPedido(pedido.id, {
        status: 'Em preparação' as any // Status temporário para aprovação
      });

      // Converter para carga usando o serviço existente
      const CargasService = require('../services/cargasServiceAPI').default;
      await CargasService.converterSolicitacaoEmCarga(pedido.solicitacao_id);

      // Atualizar status da solicitação
      if (pedido.solicitacao_id) {
        const { SolicitacoesService } = require('../services/solicitacoesService');
        await SolicitacoesService.atualizarStatus(pedido.solicitacao_id, 'APROVADO');
      }

      alert('Pedido aprovado e convertido em carga com sucesso!');
      carregarPedidos();
    } catch (error) {
      console.error('Erro ao aprovar pedido:', error);
      alert('Erro ao aprovar pedido: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Aguardando Pedido': return 'bg-orange-100 text-orange-800';
      case 'Pedido digitado': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPrioridadeColor = (prioridade: number) => {
    switch (prioridade) {
      case 5: return { bg: 'bg-red-600', text: 'Atrasado', border: 'border-red-600' };
      case 4: return { bg: 'bg-red-500', text: 'Hoje', border: 'border-red-500' };
      case 3: return { bg: 'bg-orange-500', text: '2-3 dias', border: 'border-orange-500' };
      case 2: return { bg: 'bg-yellow-500', text: '4-6 dias', border: 'border-yellow-500' };
      case 1: return { bg: 'bg-green-500', text: '7+ dias', border: 'border-green-500' };
      default: return { bg: 'bg-gray-500', text: 'Sem data', border: 'border-gray-500' };
    }
  };

  const formatarData = (dataString: string) => {
    if (!dataString) return '';
    // Converter string para Date usando fuso horário local para evitar problemas de UTC
    if (dataString.includes('T')) {
      const [ano, mes, dia] = dataString.split('T')[0].split('-').map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString('pt-BR');
    } else {
      const [ano, mes, dia] = dataString.split('-').map(Number);
      const data = new Date(ano, mes - 1, dia);
      return data.toLocaleDateString('pt-BR');
    }
  };

  const formatarDataHora = (dataString: string) => {
    if (!dataString) return '';
    const data = new Date(dataString);
    return data.toLocaleString('pt-BR');
  };

  const formatarValor = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const handleVisualizarDocumento = async (url: string, nome: string, storagePath?: string) => {
    try {
      // Se não tiver URL mas tiver storagePath, gerar URL pública
      if (!url && storagePath) {
        const { supabase } = await import('../services/supabase');
        const { data: { publicUrl } } = supabase.storage
          .from('pedidos') // Usar bucket correto para pedidos
          .getPublicUrl(storagePath);
        url = publicUrl;
      }

      // Se ainda não tiver URL, tentar gerar signed URL
      if (!url && storagePath) {
        const { supabase } = await import('../services/supabase');
        const { data, error } = await supabase.storage
          .from('pedidos') // Usar bucket correto para pedidos
          .createSignedUrl(storagePath, 3600); // URL válida por 1 hora
        
        if (!error && data?.signedUrl) {
          url = data.signedUrl;
        }
      }
      
      if (!url) {
        alert('Erro ao carregar documento: URL não disponível');
        return;
      }

      // Abrir em nova aba
      window.open(url, '_blank');
    } catch (error) {
      console.error('Erro ao visualizar documento:', error);
      alert('Erro ao carregar documento. Tente novamente.');
    }
  };

  const handleDownloadDocumento = async (url: string, nome: string, storagePath?: string) => {
    try {
      // Se não tiver URL mas tiver storagePath, gerar URL pública
      if (!url && storagePath) {
        const { supabase } = await import('../services/supabase');
        const { data: { publicUrl } } = supabase.storage
          .from('documentos')
          .getPublicUrl(storagePath);
        url = publicUrl;
      }

      // Se ainda não tiver URL, tentar gerar signed URL
      if (!url && storagePath) {
        const { supabase } = await import('../services/supabase');
        const { data, error } = await supabase.storage
          .from('documentos')
          .createSignedUrl(storagePath, 3600); // URL válida por 1 hora
        
        if (!error && data?.signedUrl) {
          url = data.signedUrl;
        }
      }
      
      if (!url) {
        alert('Erro ao baixar documento: URL não disponível');
        return;
      }

      // Fazer download
      const link = document.createElement('a');
      link.href = url;
      link.download = nome;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      alert('Erro ao baixar documento. Tente novamente.');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Pedidos de compra</h1>
        </div>

        {/* Abas */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setAbaAtiva('pendentes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                abaAtiva === 'pendentes'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Aguardando Pedido ({pedidosPendentes.length})
            </button>
            <button
              onClick={() => setAbaAtiva('digitados')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                abaAtiva === 'digitados'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pedidos Digitados ({pedidosDigitados.length})
            </button>
            <button
              onClick={() => setAbaAtiva('todos')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                abaAtiva === 'todos'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Todos os Pedidos ({pedidos.length})
            </button>
            <button
              onClick={() => setAbaAtiva('checkbox_d2')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                abaAtiva === 'checkbox_d2'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Checkbox D+2
            </button>
          </nav>
        </div>

        {/* Filtros */}
        {abaAtiva !== 'checkbox_d2' && (
          <div className="bg-white p-4 rounded-lg shadow mb-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">Todos</option>
                <option value="Aguardando Pedido">Aguardando Pedido</option>
                <option value="Pedido digitado">Pedido digitado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor</label>
              <Input
                placeholder="Buscar fornecedor..."
                value={filters.fornecedor}
                onChange={(value) => setFilters(prev => ({ ...prev, fornecedor: value }))}
              />
            </div>
            <div>
              <DatePicker
                label="Data Desejada"
                value={filters.data}
                onChange={(date) => setFilters(prev => ({ ...prev, data: date ? date.toISOString().split('T')[0] : '' }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nº Pedido WINTHOR</label>
              <div className="flex gap-2">
                <Input
                  placeholder="Digite o número..."
                  value={numpedPesquisa}
                  onChange={setNumpedPesquisa}
                />
                <Button
                  variant="primary"
                  onClick={handleBuscarProdutosPedido}
                  disabled={loadingProdutos}
                >
                  {loadingProdutos ? 'Buscando...' : 'Buscar'}
                </Button>
              </div>
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setFilters({ status: '', fornecedor: '', data: '' });
                  setNumpedPesquisa('');
                  setProdutosPedido([]);
                }}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </div>
        )}

        {/* Tabela */}
        {abaAtiva !== 'checkbox_d2' && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-gray-500">Carregando pedidos...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fornecedor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data Desejada
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Peso
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Volume
                    </th>
                    {abaAtiva === 'pendentes' && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prioridade
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPedidos.map((pedido) => {
                    const prioridade = getPrioridadeColor(pedido.prioridade);
                    return (
                      <tr 
                        key={pedido.id} 
                        className={`hover:bg-gray-50 ${
                          abaAtiva === 'pendentes' ? `border-l-4 ${prioridade.border}` : ''
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          <div>
                            <div>{pedido.codigo}</div>
                            {pedido.numero_pedido && (
                              <div className="text-xs text-gray-500">Nº: {pedido.numero_pedido}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                            <div className="font-medium">{pedido.fornecedor || 'Não informado'}</div>
                            {pedido.solicitacao && (
                              <div className="text-xs text-gray-500">
                                Solicitação: {pedido.solicitacao.codigo}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatarData(pedido.data_desejada || '')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {pedido.peso ? `${pedido.peso.toLocaleString('pt-BR')} kg` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {pedido.volume ? `${pedido.volume.toLocaleString('pt-BR')} m³` : '-'}
                        </td>
                        {abaAtiva === 'pendentes' && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white ${prioridade.bg}`}>
                              {prioridade.text}
                            </span>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(pedido.status)}`}>
                            {pedido.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {abaAtiva === 'pendentes' && pedido.status === 'Aguardando Pedido' && (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleConfirmarPedido(pedido)}
                              >
                                Digitar
                              </Button>
                            )}
                            {abaAtiva === 'digitados' && pedido.status === 'Pedido digitado' && (
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() => handleAprovarPedido(pedido)}
                              >
                                Aprovar
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingPedido(pedido);
                                setShowModal(true);
                              }}
                            >
                              Ver
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {!loading && filteredPedidos.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {abaAtiva === 'pendentes' 
                  ? 'Nenhum pedido aguardando digitação' 
                  : 'Nenhum pedido encontrado'}
              </p>
            </div>
          )}
        </div>
        )}

        {/* Produtos do Pedido WINTHOR */}
        {produtosPedido.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-hidden mt-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Produtos do Pedido {numpedPesquisa}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {produtosPedido.length} produto(s) encontrado(s)
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nº Pedido
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cod. Produto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fornecedor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Filial
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qt. Pedida
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Qt. Entregue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Preço
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {produtosPedido.map((produto, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {produto.NUMPED}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {produto.CODPROD}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {produto.FORNECEDOR}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {produto.CODFILIAL}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {produto.QTPEDIDA}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {produto.QTENTREGUE}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {produto.PCOMPRA ? `R$ ${parseFloat(produto.PCOMPRA).toFixed(2)}` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Componente Checkbox D+2 */}
        {abaAtiva === 'checkbox_d2' && (
          <CheckboxD2 filial={filialAtual} />
        )}

        {/* Estatísticas */}
        {abaAtiva !== 'checkbox_d2' && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">Total de Pedidos</div>
            <div className="text-2xl font-bold text-gray-900">{pedidos.length}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">Aguardando Pedido</div>
            <div className="text-2xl font-bold text-orange-600">
              {pedidos.filter(p => p.status === 'Aguardando Pedido').length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm font-medium text-gray-500">Pedidos Digitados</div>
            <div className="text-2xl font-bold text-purple-600">
              {pedidos.filter(p => p.status === 'Pedido digitado').length}
            </div>
          </div>
        </div>
        )}
      </div>

      
      {/* Modal de Visualização */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Detalhes do Pedido"
        size="lg"
      >
        {editingPedido && (
          <div className="space-y-6">
            {/* Informações do Pedido */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium mb-3">Informações do Pedido</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Código:</span> {editingPedido.codigo}
                </div>
                <div>
                  <span className="font-medium">Status:</span> 
                  <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(editingPedido.status)}`}>
                    {editingPedido.status}
                  </span>
                </div>
                <div>
                  <span className="font-medium">Fornecedor:</span> {editingPedido.fornecedor || 'Não informado'}
                </div>
                <div>
                  <span className="font-medium">Data Desejada:</span> {formatarData(editingPedido.data_desejada || '')}
                </div>
                {editingPedido.numero_pedido && (
                  <div>
                    <span className="font-medium">Número do Pedido:</span> {editingPedido.numero_pedido}
                  </div>
                )}
                {editingPedido.data_digitacao && (
                  <div>
                    <span className="font-medium">Digitado em:</span> {new Date(editingPedido.data_digitacao).toLocaleDateString('pt-BR')}
                  </div>
                )}
                {editingPedido.peso && (
                  <div>
                    <span className="font-medium">Peso:</span> {editingPedido.peso.toLocaleString('pt-BR')} kg
                  </div>
                )}
                {editingPedido.volume && (
                  <div>
                    <span className="font-medium">Volume:</span> {editingPedido.volume.toLocaleString('pt-BR')} m³
                  </div>
                )}
              </div>
            </div>

            {/* Seção de Documentos da Solicitação Vinculada */}
            {editingPedido.solicitacao && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3">Documentos da Solicitação Vinculada</h4>
                <div className="text-sm mb-3">
                  <div className="mb-2">
                    <span className="font-medium">Solicitação:</span> {(Array.isArray(editingPedido.solicitacao) ? editingPedido.solicitacao[0] : editingPedido.solicitacao)?.codigo}
                  </div>
                  <div className="mb-2">
                    <span className="font-medium">Solicitante:</span> {(Array.isArray(editingPedido.solicitacao) ? editingPedido.solicitacao[0] : editingPedido.solicitacao)?.solicitante?.nome || 'Não informado'}
                  </div>
                  <div>
                    <span className="font-medium">Contato:</span> {(Array.isArray(editingPedido.solicitacao) ? editingPedido.solicitacao[0] : editingPedido.solicitacao)?.solicitante?.email || 'Não informado'}
                  </div>
                </div>
                
                <div className="border-t pt-3">
                  <div className="text-sm font-medium text-gray-900 mb-3">Documentos Anexados:</div>
                  <div className="space-y-2">
                    {/* Documento principal da solicitação */}
                    <div className="flex items-center justify-between p-3 bg-white border rounded hover:bg-gray-50 cursor-pointer">
                      <div className="flex items-center space-x-3">
                        <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 1h6v8H6V5zm1 1v6h4V6H7z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <div className="text-sm font-medium">documento_fornecedor.pdf</div>
                          <div className="text-xs text-gray-500">Documento principal do fornecedor</div>
                        </div>
                        <span className="text-xs text-gray-500">(2.3 MB)</span>
                      </div>
                      <button
                        onClick={() => {
                          const solicitacao = Array.isArray(editingPedido.solicitacao) ? editingPedido.solicitacao[0] : editingPedido.solicitacao;
                          const doc = solicitacao?.documentos?.[0];
                          const url = doc?.url || '';
                          const storagePath = doc?.storagePath || '';
                          const nome = doc?.nome || 'documento_fornecedor.pdf';
                          handleVisualizarDocumento(url, nome, storagePath);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 border border-blue-300 rounded hover:bg-blue-50"
                      >
                        Visualizar
                      </button>
                      <button
                        onClick={() => {
                          const solicitacao = Array.isArray(editingPedido.solicitacao) ? editingPedido.solicitacao[0] : editingPedido.solicitacao;
                          const doc = solicitacao?.documentos?.[0];
                          const url = doc?.url || '';
                          const storagePath = doc?.storagePath || '';
                          const nome = doc?.nome || 'documento_fornecedor.pdf';
                          handleDownloadDocumento(url, nome, storagePath);
                        }}
                        className="text-green-600 hover:text-green-800 text-sm font-medium px-3 py-1 border border-green-300 rounded hover:bg-green-50 ml-2"
                      >
                        Download
                      </button>
                    </div>
                    
                    {/* Documentos adicionais */}
                    {(Array.isArray(editingPedido.solicitacao) ? editingPedido.solicitacao[0] : editingPedido.solicitacao)?.documentos && (Array.isArray(editingPedido.solicitacao) ? editingPedido.solicitacao[0] : editingPedido.solicitacao)?.documentos.length > 0 && (
                      (Array.isArray(editingPedido.solicitacao) ? editingPedido.solicitacao[0] : editingPedido.solicitacao)?.documentos.map((doc: any, index: number) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white border rounded hover:bg-gray-50 cursor-pointer">
                          <div className="flex items-center space-x-3">
                            <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 1h6v8H6V5zm1 1v6h4V6H7z" clipRule="evenodd" />
                            </svg>
                            <div>
                              <div className="text-sm font-medium">{doc.nome}</div>
                              <div className="text-xs text-gray-500">Documento adicional</div>
                            </div>
                            <span className="text-xs text-gray-500">({(doc.tamanho ? (doc.tamanho / (1024 * 1024)).toFixed(1) : '1.2')} MB)</span>
                          </div>
                          <button
                            onClick={() => {
                              const solicitacao = Array.isArray(editingPedido.solicitacao) ? editingPedido.solicitacao[0] : editingPedido.solicitacao;
                              const docFromSolicitacao = solicitacao?.documentos?.[index] || doc;
                              handleVisualizarDocumento(docFromSolicitacao.url || '', docFromSolicitacao.nome, docFromSolicitacao.storagePath);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 border border-blue-300 rounded hover:bg-blue-50"
                          >
                            Visualizar
                          </button>
                          <button
                            onClick={() => {
                              const solicitacao = Array.isArray(editingPedido.solicitacao) ? editingPedido.solicitacao[0] : editingPedido.solicitacao;
                              const docFromSolicitacao = solicitacao?.documentos?.[index] || doc;
                              handleDownloadDocumento(docFromSolicitacao.url || '', docFromSolicitacao.nome, docFromSolicitacao.storagePath);
                            }}
                            className="text-green-600 hover:text-green-800 text-sm font-medium px-3 py-1 border border-green-300 rounded hover:bg-green-50 ml-2"
                          >
                            Download
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Documentos Digitados do Pedido */}
            {editingPedido.documentos_digitados && editingPedido.documentos_digitados.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3">Documentos Digitados do Pedido</h4>
                <div className="space-y-2">
                  {editingPedido.documentos_digitados.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white border rounded hover:bg-gray-50 cursor-pointer">
                      <div className="flex items-center space-x-3">
                        <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 1h6v8H6V5zm1 1v6h4V6H7z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <div className="text-sm font-medium">{doc.nome}</div>
                          <div className="text-xs text-gray-500">Documento digitado</div>
                        </div>
                        <span className="text-xs text-gray-500">({(doc.tamanho ? (doc.tamanho / (1024 * 1024)).toFixed(1) : '0.5')} MB)</span>
                      </div>
                      <button
                        onClick={() => handleVisualizarDocumento(doc.url || '', doc.nome)}
                        className="text-green-600 hover:text-green-800 text-sm font-medium px-3 py-1 border border-green-300 rounded hover:bg-green-50"
                      >
                        Visualizar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Botões de Ação */}
            <div className="flex justify-end pt-4 border-t">
              <Button onClick={() => setShowModal(false)}>
                Fechar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Confirmação de Pedido */}
      <Modal
        isOpen={showConfirmarModal}
        onClose={() => {
          setShowConfirmarModal(false);
          setConfirmarPedido(null);
          setNumeroPedido('');
          setArquivoSelecionado(null);
        }}
        title="Digitar Pedido"
        size="md"
      >
        <div className="space-y-4">
          {confirmarPedido && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Informações do Pedido:</h4>
              <div className="text-sm space-y-1">
                <div><span className="font-medium">Código:</span> {confirmarPedido.codigo}</div>
                <div><span className="font-medium">Fornecedor:</span> {confirmarPedido.fornecedor || 'Não informado'}</div>
                <div><span className="font-medium">Data Desejada:</span> {formatarData(confirmarPedido.data_desejada || '')}</div>
                {confirmarPedido.peso && (
                  <div><span className="font-medium">Peso:</span> {confirmarPedido.peso.toLocaleString('pt-BR')} kg</div>
                )}
                {confirmarPedido.volume && (
                  <div><span className="font-medium">Volume:</span> {confirmarPedido.volume.toLocaleString('pt-BR')} m³</div>
                )}
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Número do Pedido *
            </label>
            <Input
              value={numeroPedido}
              onChange={setNumeroPedido}
              placeholder="Digite o número do pedido..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Anexar Documento *
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setArquivoSelecionado(file);
                  } else {
                    setArquivoSelecionado(null);
                  }
                }}
                className="w-full text-sm text-gray-500 file:mr-4 file:font-medium"
              />
              <p className="text-xs text-gray-500 mt-1">
                PDF, DOC, DOCX, XLS, XLSX, PNG, JPG (máx. 10MB)
              </p>
            </div>
          </div>

          {/* Seção de Documentos da Solicitação Vinculada */}
          {confirmarPedido?.solicitacao && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Documentos da Solicitação Vinculada</h4>
              <div className="text-sm mb-2">
                <span className="font-medium">Solicitação:</span> {confirmarPedido.solicitacao.codigo}
              </div>
              <div className="text-sm text-gray-600 mb-3">
                <span className="font-medium">Solicitante:</span> {confirmarPedido.solicitacao.solicitante?.nome || 'Não informado'}
              </div>
              
              <div className="border-t pt-3">
                <div className="text-sm font-medium text-gray-900 mb-2">Documentos Anexados:</div>
                <div className="space-y-2">
                  {/* Documento principal da solicitação */}
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer">
                    <div className="flex items-center space-x-2">
                      <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 1h6v8H6V5zm1 1v6h4V6H7z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm">documento_fornecedor.pdf</span>
                      <span className="text-xs text-gray-500">(2.3 MB)</span>
                    </div>
                    <button
                      onClick={() => {
                        const solicitacao = Array.isArray(confirmarPedido.solicitacao) ? confirmarPedido.solicitacao[0] : confirmarPedido.solicitacao;
                        const doc = solicitacao?.documentos?.[0];
                        const url = doc?.url || '';
                        const storagePath = doc?.storagePath || '';
                        const nome = doc?.nome || 'documento_fornecedor.pdf';
                        handleVisualizarDocumento(url, nome, storagePath);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Visualizar
                    </button>
                    <button
                      onClick={() => {
                        const doc = confirmarPedido.solicitacao?.documentos?.[0];
                        const url = doc?.url || '';
                        const storagePath = doc?.storagePath || '';
                        const nome = doc?.nome || 'documento_fornecedor.pdf';
                        handleDownloadDocumento(url, nome, storagePath);
                      }}
                      className="text-green-600 hover:text-green-800 text-sm font-medium ml-2"
                    >
                      Download
                    </button>
                  </div>
                  
                  {/* Documentos adicionais */}
                  {(Array.isArray(confirmarPedido.solicitacao) ? confirmarPedido.solicitacao[0] : confirmarPedido.solicitacao)?.documentos && (Array.isArray(confirmarPedido.solicitacao) ? confirmarPedido.solicitacao[0] : confirmarPedido.solicitacao)?.documentos.length > 0 && (
                    (Array.isArray(confirmarPedido.solicitacao) ? confirmarPedido.solicitacao[0] : confirmarPedido.solicitacao)?.documentos.map((doc: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer">
                        <div className="flex items-center space-x-2">
                          <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 1h6v8H6V5zm1 1v6h4V6H7z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm">{doc.nome}</span>
                          <span className="text-xs text-gray-500">({(doc.tamanho ? (doc.tamanho / (1024 * 1024)).toFixed(1) : '1.2')} MB)</span>
                        </div>
                        <button
                          onClick={() => {
                            const solicitacao = Array.isArray(confirmarPedido.solicitacao) ? confirmarPedido.solicitacao[0] : confirmarPedido.solicitacao;
                            const docFromSolicitacao = solicitacao?.documentos?.[index] || doc;
                            handleVisualizarDocumento(docFromSolicitacao.url || '', docFromSolicitacao.nome, docFromSolicitacao.storagePath);
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Visualizar
                        </button>
                        <button
                          onClick={() => {
                            const solicitacao = Array.isArray(confirmarPedido.solicitacao) ? confirmarPedido.solicitacao[0] : confirmarPedido.solicitacao;
                            const docFromSolicitacao = solicitacao?.documentos?.[index] || doc;
                            handleDownloadDocumento(docFromSolicitacao.url || '', docFromSolicitacao.nome, docFromSolicitacao.storagePath);
                          }}
                          className="text-green-600 hover:text-green-800 text-sm font-medium ml-2"
                        >
                          Download
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowConfirmarModal(false);
                setConfirmarPedido(null);
                setNumeroPedido('');
                setArquivoSelecionado(null);
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSalvarConfirmacao}
              disabled={!numeroPedido.trim() || !arquivoSelecionado}
            >
              Confirmar Pedido
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const PedidosProtegido = () => (
  <AbaProtegida nomeAba="Gestão de Pedidos">
    <Pedidos />
  </AbaProtegida>
);

export default PedidosProtegido;
